// Consent persistence: one structured first-party cookie, validated before it is trusted.
import { isObject } from './utils.js';

/** Bump when the stored shape changes; older records then count as missing and the banner returns. */
export const STORAGE_VERSION = 1;

const DAY_MS = 24 * 60 * 60 * 1000;

export function createConsentStorage({ doc, name, domain, secure }) {
  function setCookie(value, maxAgeSeconds) {
    let cookie = `${name}=${value}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
    if (domain) cookie += `; Domain=${domain}`;
    if (secure) cookie += '; Secure';
    try {
      doc.cookie = cookie;
    } catch {
      // Cookies unavailable (e.g. sandboxed iframe): the decision still applies to this page view.
    }
  }

  return {
    /** The parsed cookie value, or null. Not trusted yet — pass it through parseRecord(). */
    read() {
      let cookies;
      try {
        cookies = doc.cookie;
      } catch {
        return null;
      }
      for (const pair of cookies.split(';')) {
        const separator = pair.indexOf('=');
        if (separator === -1 || pair.slice(0, separator).trim() !== name) continue;
        try {
          return JSON.parse(decodeURIComponent(pair.slice(separator + 1).trim()));
        } catch {
          return null;
        }
      }
      return null;
    },

    write(record, maxAgeSeconds) {
      setCookie(encodeURIComponent(JSON.stringify(record)), maxAgeSeconds);
    },

    clear() {
      setCookie('', 0);
    },
  };
}

/** When a decision saved at `updatedAt` expires, `months` calendar months later. */
export function consentExpiry(updatedAt, months) {
  const date = new Date(updatedAt);
  const day = date.getUTCDate();
  date.setUTCMonth(date.getUTCMonth() + months);
  if (date.getUTCDate() !== day) date.setUTCDate(0); // 31 Jan + 1 month → end of Feb, not 3 Mar
  return date.getTime();
}

/**
 * Returns a clean copy of a stored record, or null when it is malformed, from another schema or
 * policy version, expired, or dated in the future — all of which mean the visitor decides again.
 */
export function parseRecord(value, { policyVersion, lifetimeMonths, now = Date.now() }) {
  if (!isObject(value) || value.version !== STORAGE_VERSION || value.policyVersion !== policyVersion) return null;

  const { choices, timestamps } = value;
  if (!isObject(choices) || choices.functional !== true) return null;
  if (typeof choices.analytics !== 'boolean' || typeof choices.marketing !== 'boolean') return null;

  if (!isObject(timestamps)) return null;
  const { createdAt, updatedAt, bannerOpenedAt } = timestamps;
  if (![createdAt, updatedAt, bannerOpenedAt].every(isTimestamp)) return null;
  if (createdAt > updatedAt || updatedAt > now + DAY_MS) return null;
  if (now >= consentExpiry(updatedAt, lifetimeMonths)) return null;

  return {
    version: STORAGE_VERSION,
    policyVersion,
    choices: { functional: true, analytics: choices.analytics, marketing: choices.marketing },
    timestamps: { createdAt, updatedAt, bannerOpenedAt },
  };
}

function isTimestamp(value) {
  return Number.isSafeInteger(value) && value >= 0;
}
